import { type ReactNode, createContext, useContext, useState } from 'react';
import type { IAppDetailsAppData } from '../common-types/appDetailsAppData';
import type { IWidget } from '../common-types/widget';
import type { IAppDetailsTranslations, IDynamicContent } from 'index';

type AppDetailsEventHandlers = {
  handleRecommendButtonClick?: () => void;
  handleDownloadButtonClick?: () => void;
  handleFavouriteButtonClick?: {
    handleAddFavouriteClick: () => void;
    handleRemoveFavouriteClick: () => void;
  };
  handlePlatformTabChange?: (platformName: string) => void;
  handleScoreTabChange?: (tabName: string) => void;
  handleRequestDynamicContent?: (reviewId: number) => void;
};

type AppDetailsProviderProps = {
  app?: IAppDetailsAppData;
  widgets?: IWidget[];
  initialIsAppFavourited?: boolean;
  eventHandlers?: AppDetailsEventHandlers;
  dynamicContentApiUrl?: string;
  dynamicContent?: IDynamicContent;
  children?: ReactNode;
  translations?: IAppDetailsTranslations;
};

const useAppDetailsController = (
  app?: IAppDetailsAppData,
  widgets?: IWidget[],
  initialIsAppFavourited?: boolean,
  dynamicContentApiUrl?: string,
  eventHandlers?: AppDetailsEventHandlers,
  dynamicContent?: IDynamicContent,
  translations?: IAppDetailsTranslations
) => {
  const [isRecommendDialogOpen, setIsRecommendDialogOpen] = useState(false);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [isAppFavourited, setIsAppFavourited] = useState(
    initialIsAppFavourited ?? false
  );

  const handleRecommendButtonClick = () => {
    if (eventHandlers?.handleRecommendButtonClick) {
      eventHandlers?.handleRecommendButtonClick();
    }
    setIsRecommendDialogOpen(true);
  };

  const handleDownloadButtonClick = () => {
    if (eventHandlers?.handleDownloadButtonClick) {
      eventHandlers?.handleDownloadButtonClick();
    }
    setIsDownloadDialogOpen(true);
  };

  return {
    setIsDownloadDialogOpen,
    isDownloadDialogOpen,
    setIsRecommendDialogOpen,
    isRecommendDialogOpen,
    isAppFavourited,
    setIsAppFavourited,
    app,
    widgets,
    dynamicContentApiUrl,
    eventHandlers: {
      ...eventHandlers,
      ...(eventHandlers?.handleRecommendButtonClick && {
        handleRecommendButtonClick,
      }),
      ...(eventHandlers?.handleDownloadButtonClick && {
        handleDownloadButtonClick,
      }),
    },
    dynamicContent,
    translations,
  };
};

const AppDetailsContext = createContext<
  ReturnType<typeof useAppDetailsController>
>({
  app: undefined,
  widgets: undefined,
  setIsDownloadDialogOpen: () => {},
  isDownloadDialogOpen: false,
  setIsRecommendDialogOpen: () => {},
  isRecommendDialogOpen: false,
  isAppFavourited: false,
  setIsAppFavourited: () => {},
  dynamicContentApiUrl: undefined,
  eventHandlers: {
    handleRecommendButtonClick: () => {},
    handleDownloadButtonClick: () => {},
  },
  dynamicContent: undefined,
  translations: undefined,
});

export const AppDetailsProvider = ({
  app,
  widgets,
  initialIsAppFavourited,
  dynamicContentApiUrl,
  eventHandlers,
  dynamicContent,
  translations,
  children,
}: AppDetailsProviderProps) => (
  <AppDetailsContext.Provider
    value={useAppDetailsController(
      app,
      widgets,
      initialIsAppFavourited,
      dynamicContentApiUrl,
      eventHandlers,
      dynamicContent,
      translations
    )}
  >
    {children}
  </AppDetailsContext.Provider>
);

export const useAppDetails = () => useContext(AppDetailsContext);
